import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { isOurCloudinaryUrl } from "../../lib/cloudinary";
import { Loader2 } from "lucide-react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Above admin product form modal (z-[60]). Default AlertDialog uses z-50. */
const ALERT_Z = "z-[70]";

/**
 * Confirm removing an image from a product; optionally destroy on Cloudinary.
 */
export default function RemoveImageDialog({
    open,
    onOpenChange,
    imageUrl,
    onRemoved,
    variant = "product",
}) {
    const isLibrary = variant === "library";
    const [deleteFromCloud, setDeleteFromCloud] = useState(isLibrary);
    const [busy, setBusy] = useState(false);
    const [usage, setUsage] = useState(null);

    const canDeleteFromCloud = isOurCloudinaryUrl(imageUrl);

    useEffect(() => {
        if (!open || !imageUrl) {
            setDeleteFromCloud(isLibrary);
            setUsage(null);
            return;
        }
        if (!canDeleteFromCloud) return;

        let cancelled = false;
        api.get("/admin/cloudinary/image-usage", { params: { url: imageUrl } })
            .then(({ data }) => {
                if (!cancelled) setUsage(data);
            })
            .catch(() => {
                if (!cancelled) setUsage(null);
            });
        return () => {
            cancelled = true;
        };
    }, [open, imageUrl, canDeleteFromCloud, isLibrary]);

    const handleConfirm = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const shouldDeleteCloud = isLibrary
                ? canDeleteFromCloud
                : deleteFromCloud && canDeleteFromCloud;
            if (shouldDeleteCloud) {
                await api.delete("/admin/cloudinary/images", { data: { url: imageUrl } });
            }
            await onRemoved?.();
            onOpenChange(false);
        } catch (err) {
            const detail = err.response?.data?.detail;
            window.alert(formatApiError(detail) || err.message || "Could not remove image.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <AlertDialogPrimitive.Portal>
                <AlertDialogPrimitive.Overlay
                    className={cn(
                        "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                        ALERT_Z,
                    )}
                />
                <AlertDialogPrimitive.Content
                    className={cn(
                        "fixed left-[50%] top-[50%] grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 rounded-3xl border-sage-300/40",
                        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                        ALERT_Z,
                    )}
                    data-testid="remove-image-dialog"
                >
                <AlertDialogHeader>
                    <AlertDialogTitle className="font-serif text-xl text-ink-900">
                        {isLibrary ? "Delete from media library?" : "Remove this image?"}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-left text-ink-600 space-y-3">
                        {!isLibrary && (
                            <span className="block">
                                It will be removed from this product. The product is not saved until you click Update or Create.
                            </span>
                        )}
                        {isLibrary && (
                            <span className="block">
                                This removes the file from Cloudinary. Products that still use this URL will show a broken image until you update them.
                            </span>
                        )}
                        {canDeleteFromCloud && !isLibrary && (
                            <label className="flex items-start gap-3 rounded-xl border border-sage-300/40 bg-ivory-50/80 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={deleteFromCloud}
                                    onChange={(e) => setDeleteFromCloud(e.target.checked)}
                                    disabled={busy}
                                    data-testid="remove-image-delete-cloud"
                                />
                                <span className="text-sm text-ink-800">
                                    Also delete this image from our cloud storage (Cloudinary). This cannot be undone.
                                </span>
                            </label>
                        )}
                        {canDeleteFromCloud && isLibrary && (
                            <span className="block text-sm text-ink-800 rounded-xl border border-sage-300/40 bg-ivory-50/80 p-3">
                                The file will be permanently deleted from Cloudinary. This cannot be undone.
                            </span>
                        )}
                        {!canDeleteFromCloud && !isLibrary && (
                            <span className="block text-xs text-ink-500">
                                This URL is not hosted on our Cloudinary account, so it can only be removed from the product.
                            </span>
                        )}
                        {(isLibrary ? canDeleteFromCloud : deleteFromCloud) && usage?.count > 0 && (
                            <span className="block text-xs text-amber-800 bg-amber-50 border border-amber-200/80 rounded-lg px-3 py-2">
                                This image is used on {usage.count} product(s)
                                {usage.productNames?.length
                                    ? ` including "${usage.productNames.slice(0, 3).join('", "')}"`
                                    : ""}
                                . Deleting from cloud will break it everywhere.
                            </span>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="gap-2 sm:gap-2">
                    <AlertDialogCancel disabled={busy} className="rounded-full">
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleConfirm}
                        disabled={busy}
                        className="rounded-full bg-blush-400 hover:bg-blush-500"
                        data-testid="remove-image-confirm"
                    >
                        {busy ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
                                Working…
                            </>
                        ) : isLibrary ? (
                            "Delete"
                        ) : (
                            "Remove"
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
                </AlertDialogPrimitive.Content>
            </AlertDialogPrimitive.Portal>
        </AlertDialog>
    );
}
